import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Report.scss";
import { Reportmenupos } from "./Reportmenupos";
import { Daterangepos } from "../common/Daterangepos";
import TableModal from "../common/Modals/tableColumnPos";
import { _Api } from "../common/api/_call";
import DailySaleReportTableHead from "./DailySaleReportTableHead";
import { useReportColumnFilters } from "./useReportColumnFilters";
import { getNextSortConfig, sortSaleMonitoringRows } from "./saleMonitoringReportTableConfig";
import { useReportStickyColumns } from "./useReportStickyColumns";
import { possalemonitoringreport_head } from "../../components/common/Tablehead";
import { useTranslation } from "react-i18next";
import { Pagination } from "../common/Pagination";
import TableLoader from "../../admin/common/TableLoader";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import Select from "react-select";

const TABLE_NAME = "report_salemonitoring_tbl";
const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const limitOptions = [
  { label: 100, value: 100 },
  { label: 200, value: 200 },
  { label: 300, value: 300 },
];

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

// An order can hold many items / collections: cap the width and let the list wrap onto more
// lines instead of widening the column. white-space is set here because report cells don't wrap.
const LIST_CELL_STYLE = {
  maxWidth: 200,
  minWidth: 120,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  lineHeight: 1.3,
};
const ListCell = ({ values }) => {
  const list = (values || []).filter(Boolean);
  if (!list.length) return "";
  return <div style={LIST_CELL_STYLE}>{list.join(", ")}</div>;
};

const SaleMonitoringreport = () => {
  const { t } = useTranslation();
  const base_assets = process.env.REACT_APP_BASE_ASSETS_URL;
  const [newcolumndata, setnewcolumndata] = useState(possalemonitoringreport_head);
  const [datefilter, setDatefilter] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [limit, setLimit] = useState(100);
  const [filtername, setFiltername] = useState("");
  const [loader, setLoader] = useState(false);
  const [sortConfig, setSortConfig] = useState({ columnIndex: null, direction: "asc" });
  const [state, setstate] = useState({ totalPages: 0, currentPage: 1 });
  const { totalPages, currentPage } = state;

  const activeTableColumns = newcolumndata?.length ? newcolumndata : possalemonitoringreport_head;
  // the POS column modal stores status; hide those columns in header and body alike
  const headColumns = useMemo(
    () =>
      activeTableColumns.map((col) =>
        col?.status === false ? { ...col, class: `${col.class || ""} d-none` } : col
      ),
    [activeTableColumns]
  );
  const cellClass = (index, extra = "") =>
    [extra, activeTableColumns[index]?.status === false ? "d-none" : ""].filter(Boolean).join(" ");

  const columnFilters = useReportColumnFilters(rows, { customer: "customer", sku: "skus", "sale person": "sales_person", user: "user" });
  const sortedRows = useMemo(() => sortSaleMonitoringRows(columnFilters.rows, sortConfig), [columnFilters.rows, sortConfig]);

  const reportTableContainerRef = useRef(null);
  useReportStickyColumns(reportTableContainerRef, [sortedRows, activeTableColumns]);

  const callcolumns = async () => {
    try {
      const columnhead = await _Api({ name: TABLE_NAME }, `api/v1/Inventory/common/getColumnHeads`);
      const saved = columnhead?.data?.data;
      if (columnhead.code === 200 && saved?.length) {
        // Keep the current column list and only reuse each column's saved visibility (matched by
        // name), so settings saved before a column was added/removed can't misalign the table.
        const savedStatus = new Map(saved.map((col) => [col?.name, col?.status]));
        setnewcolumndata(
          possalemonitoringreport_head.map((col) =>
            savedStatus.has(col.name) ? { ...col, status: savedStatus.get(col.name) } : col
          )
        );
      }
    } catch (err) {
      console.log(err, "err");
    }
  };
  useEffect(() => {
    callcolumns();
  }, []);

  const getSaleMonitoring = async (current = 1, pageLimit = limit, search = filtername) => {
    setLoader(true);
    try {
      const postdata = {
        search: search || "",
        start_date: datefilter ? datefilter[0] : "",
        end_date: datefilter ? datefilter[1] : "",
        limit: pageLimit,
        skip: (current - 1) * pageLimit,
      };
      const res = await _Api(postdata, `api/v1/POS/report/saleMonitoring`);
      if (res?.code === 200) {
        setRows(res?.data || []);
        setTotals(res?.totals || {});
        setstate({
          totalPages: Math.ceil((Number(res?.count) || 0) / pageLimit),
          currentPage: current,
        });
      } else {
        setRows([]);
        setTotals({});
        setstate({ totalPages: 0, currentPage: 1 });
      }
    } catch (err) {
      console.log(err, "err");
    }
    setLoader(false);
  };

  useEffect(() => {
    if (datefilter[0] && datefilter[1]) {
      getSaleMonitoring(1);
    }
    // eslint-disable-next-line
  }, [datefilter]);

  const filterbyname = (search) => getSaleMonitoring(1, limit, search);
  const changeLimit = (value) => {
    setLimit(value);
    getSaleMonitoring(1, value);
  };

  const downloadexclsheet = () => {
    const header = possalemonitoringreport_head.map((col) => col?.lable);
    const body = sortedRows.map((row, key) => [
      (currentPage - 1) * limit + key + 1,
      row?.date,
      row?.ref,
      row?.sales_person,
      row?.customer,
      row?.SKU,
      row?.item,
      row?.collection,
      row?.retail,
      row?.sale,
      row?.discount_percent,
      row?.discount,
      row?.status,
      row?.user,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([["Sale Monitoring"], ["Sale Price Monitoring"], header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Records");
    const fileContent = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    FileSaver.saveAs(new Blob([fileContent], { type: fileType }), "sale_monitoring_report");
  };

  // Discount % and Discount Amount columns are always shown in red
  const DISCOUNT_CLASS = "danger-text";

  return (
    <>
      <div className="main-body">
        <div className="main-body-row">
          <div className="main-body-row-left-col p-0 w-100">
            <div className="catalog-area bg-white page-menu-block Dailysalereport">
              <div className="Dailysalereport-header">
                <div className="Dailysalereport-header-left">
                  <h3 className="common-navigation-header-left-heading Dailysalereport-head">
                    <Link to="" className="gray-mid text-decoration-none Dailysalereport-head-report">{t("REPORT")}</Link>
                    <img className="Dailysalereport-head-arrow mx-3" src={base_assets + "images/pos/report/arrow-right.png"} alt="" />
                    <span className="Dailysalereport-head-pagename">{t("Sale Monitoring")}</span>
                  </h3>
                </div>
                <div className="Dailysalereport-header-reportdetail">
                  <div className="Dailysalereport-header-reportdetail-box">
                    <div className="Dailysalereport-header-reportdetail-box-inner">
                      <img className="Dailysalereport-header-reportdetail-box-inner-img" src={base_assets + "images/pos/report/stock-img.png"} alt="" />
                    </div>
                    <div className="Dailysalereport-header-reportdetail-box-text">
                      <span className="Dailysalereport-header-reportdetail-box-text-inner">{t("Retail Price")}</span>
                      <span className="Dailysalereport-header-reportdetail-box-text-data lightblue">{totals?.retail ?? 0}</span>
                    </div>
                  </div>
                  <div className="Dailysalereport-header-reportdetail-box">
                    <div className="Dailysalereport-header-reportdetail-box-inner">
                      <img className="Dailysalereport-header-reportdetail-box-inner-img" src={base_assets + "images/pos/report/value-img.png"} alt="" />
                    </div>
                    <div className="Dailysalereport-header-reportdetail-box-text">
                      <span className="Dailysalereport-header-reportdetail-box-text-inner">{t("Sale Price")}</span>
                      <span className="Dailysalereport-header-reportdetail-box-text-data maingreen">{totals?.sale ?? 0}</span>
                    </div>
                  </div>
                  <div className="Dailysalereport-header-reportdetail-box">
                    <div className="Dailysalereport-header-reportdetail-box-inner">
                      <img className="Dailysalereport-header-reportdetail-box-inner-img" src={base_assets + "images/pos/report/value-img.png"} alt="" />
                    </div>
                    <div className="Dailysalereport-header-reportdetail-box-text">
                      <span className="Dailysalereport-header-reportdetail-box-text-inner">
                        {t("Discount")} ({formatPercent(totals?.discount_percent)})
                      </span>
                      <span className={`Dailysalereport-header-reportdetail-box-text-data ${DISCOUNT_CLASS}`}>{totals?.discount ?? 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="Dailysalereport-content-area">
                <div className="Dailysalereport-content-area-header">
                  <div className="Dailysalereport-content-area-header-inner">
                    <Reportmenupos Reportname={"Sale Monitoring"} />
                    <Select
                      className="limit-select short mx-3"
                      options={limitOptions}
                      value={{ label: limit, value: limit }}
                      onChange={(e) => changeLimit(e.value)}
                    />
                    <div className="date"> <Daterangepos setDatefilter={setDatefilter} /></div>
                  </div>
                  <div className="catalog-area-header-area-control-right">
                    <div className="com-search-area">
                      <input
                        type="text"
                        value={filtername}
                        placeholder={t("Search by Ref No / SKU")}
                        onKeyDown={(e) => e.key === "Enter" && filterbyname(e.target.value)}
                        onChange={(e) => setFiltername(e.target.value)}
                      />
                      {filtername ? <img className="com-search-area-clear" src={base_assets + "images/icons/False.png"} onClick={() => { setFiltername(""); filterbyname(""); }} alt="" /> : ""}
                      <img onClick={() => filterbyname(filtername)} className="com-search-area-img" src={base_assets + "images/pos/icons/magnifying-glass.png"} alt="" />
                    </div>
                    <div className="dropdown Dailysalereport-content-area-header-leftdropdown">
                      <button className="btn-style" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false"><img src={base_assets + "images/icons/ellipsis-circular.png"} alt="" /></button>
                      <ul className="dropdown-menu Dailysalereport-content-area-header-leftdropdown-menu" aria-labelledby="dropdownMenuButton1">
                        <li onClick={downloadexclsheet}>
                          <Link className="dropdown-item Dailysalereport-content-area-header-leftdropdown-menu-item" to="#">
                            <div className="dropdown-menu-img">
                              <img src={base_assets + "images/pos/report/export-icon.png"} alt="" />
                            </div>
                            {t("Export")}</Link>
                        </li>
                        <li data-bs-toggle="modal" data-bs-target="#columnModal">
                          <Link className="dropdown-item Dailysalereport-content-area-header-leftdropdown-menu-item" to="#">
                            <div className="dropdown-menu-img">
                              <img src={base_assets + "images/icons/column-icon.png"} alt="" />
                            </div>
                            {t("Column")}</Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="Dailysalereport-content-area-main" ref={reportTableContainerRef}>
                  <div className="main-body-main-table-wrap Dailysalereport-table position-relative" id="table-scroll">
                    <table id="sale_monitoring_report" className="common-table first-row-gray w-100 Dailysalereport-table-main daily-sale-report-table">
                      <DailySaleReportTableHead
                        tablehead={headColumns}
                        sortConfig={sortConfig}
                        onSort={(index, direction) => setSortConfig((current) => getNextSortConfig(current, index, direction))}
                        columnFilters={columnFilters}
                      />
                      <tbody>
                        {/* hide the previous rows while loading so the loader under the header is visible */}
                        {!loader && sortedRows.map((row, key) => {
                          return (
                            <tr key={row?.order_id || key}>
                              <td className={cellClass(0)}>{(currentPage - 1) * limit + key + 1}</td>
                              <td className={cellClass(1)}>{row?.date || ""}</td>
                              <td className={cellClass(2, "link-color")}>{row?.ref || ""}</td>
                              <td className={cellClass(3)}>{row?.sales_person || ""}</td>
                              <td className={cellClass(4)}>{row?.customer || ""}</td>
                              <td className={cellClass(5)} title={(row?.skus || []).join(", ")}>{row?.SKU ?? 0}</td>
                              <td className={cellClass(6)}><ListCell values={row?.items} /></td>
                              <td className={cellClass(7)}><ListCell values={row?.collections} /></td>
                              <td className={cellClass(8)}>{row?.retail_formatted ?? 0}</td>
                              <td className={cellClass(9)}>{row?.sale_formatted ?? 0}</td>
                              <td className={cellClass(10, DISCOUNT_CLASS)}>{formatPercent(row?.discount_percent)}</td>
                              <td className={cellClass(11, DISCOUNT_CLASS)}>{row?.discount_formatted ?? 0}</td>
                              <td className={cellClass(12)}>{row?.status || ""}</td>
                              <td className={cellClass(13)}>{row?.user || ""}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className={cellClass(0)}></td>
                          <td className={cellClass(1)}></td>
                          <td className={cellClass(2)}></td>
                          <td className={cellClass(3)}></td>
                          <td className={cellClass(4)}></td>
                          <td className={cellClass(5)}>{totals?.sku_count ?? 0}</td>
                          <td className={cellClass(6)}></td>
                          <td className={cellClass(7)}></td>
                          <td className={cellClass(8)}>{totals?.retail ?? 0}</td>
                          <td className={cellClass(9)}>{totals?.sale ?? 0}</td>
                          <td className={cellClass(10, DISCOUNT_CLASS)}>
                            {formatPercent(totals?.discount_percent)}
                          </td>
                          <td className={cellClass(11, DISCOUNT_CLASS)}>{totals?.discount ?? 0}</td>
                          <td className={cellClass(12)}></td>
                          <td className={cellClass(13)}></td>
                        </tr>
                      </tfoot>
                    </table>
                    {loader && <TableLoader />}
                  </div>
                </div>
                {rows.length > 0 ? (
                  <Pagination
                    total={totalPages}
                    current={currentPage}
                    pagination={(crPage) => getSaleMonitoring(crPage)}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </div>
        <TableModal tablehead={possalemonitoringreport_head} tblName={TABLE_NAME} setnewcolumndata={setnewcolumndata} />
      </div>
    </>
  );
};
export default SaleMonitoringreport;
